import { Button } from '@iwoca/orion';
import { FormattedMessage } from 'react-intl';

import { getNumericValueFromString } from './getNumericValueFromString';
import { TurnoverField } from './TurnoverField';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import { useCreateNewApplication } from '../../../multiLoanDashboard/NewOutstandingView/CreateNewApplicationForm/useCreateNewApplication';

type TFormValues = {
    turnover: string;
    duration:
        'few_months' | 'ongoing' | 'one_to_two_years' | 'three_plus_years';
};

export const RepaymentReductionForm = ({
    outstandingPrincipal,
    companyTurnover,
    onSuccess
}: {
    outstandingPrincipal: number;
    companyTurnover: number;
    onSuccess: () => void;
}) => {
    const stateKey = useStateKey();
    const createNewApplicationMutation = useCreateNewApplication(stateKey);

    if (createNewApplicationMutation.error)
        throw createNewApplicationMutation.error;

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();

        const form = event.target as HTMLFormElement;
        const formData = new FormData(form);
        const data = Object.fromEntries(
            formData.entries()
        ) as unknown as TFormValues;

        if (!form.checkValidity()) {
            const firstInvalid = form.querySelector<HTMLElement>(':invalid');
            if (firstInvalid) {
                firstInvalid.focus();
                return;
            }
        }

        const application = {
            amount: outstandingPrincipal,
            requested_cash_amount: 0,
            product_type: 'credit_facility' as const,
            duration: { unit: 'months' as const, amount: 60 },
            qualitative_duration: 'three_plus_years' as const,
            last_12_months_turnover: getNumericValueFromString(data.turnover)
        };

        createNewApplicationMutation.mutate(application, {
            onSuccess: () => onSuccess()
        });
    };

    return (
        <form
            data-testid="repayment-reduction-form"
            onSubmit={handleSubmit}
            className="w-full space-y-xl"
            noValidate
        >
            <TurnoverField defaultValue={companyTurnover} />
            <Button
                type="submit"
                loading={createNewApplicationMutation.isPending}
                className="w-full"
            >
                <FormattedMessage
                    id="_.topup_choice.check_my_eligibility"
                    defaultMessage="Check my eligibility"
                />
            </Button>
        </form>
    );
};
